import type {
  ExpressionSpecification,
  GeoJSONSource,
  Map as MapLibreMap,
  MapMouseEvent,
} from 'maplibre-gl';
import { Map as MapLibreMapCtor, NavigationControl } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { useEffect, useRef, useState } from 'react';

// Side effect only, and it must run before the first `new Map(...)` — see
// `FlightMap.tsx` and the module itself.
import './maplibre-worker';

import { motionDuration } from '@/lib/motion';

import { getBasemap, preferredBasemapId, STYLE_LOAD_TIMEOUT_MS } from './basemaps';
import {
  buildRouteCollection,
  boundsForRoutes,
  ROUTE_HIGHLIGHT_WIDTH,
  ROUTE_OPACITY,
  ROUTE_WIDTH,
  routeColour,
  type TrackRoute,
} from './overlay';
import { prefersDark } from './theme';

/**
 * Every flight on one map.
 *
 * The flight page's `FlightMap` is the opposite instrument: one track, in
 * detail, coloured by a per-point metric, with a scrubber. This one draws a
 * whole logbook's worth of tracks at once and lets two things carry the
 * information — the overlap, so the circuits at the home field come out as a
 * knot and the two trips to the South Island as two thin lines, and the
 * colour, which `FlightsOverviewMap` sets per route from where the flight
 * falls in the drawn set's date range: oldest to newest across a palette,
 * rather than the single accent colour every other route-level thing in this
 * app uses. This component does not compute that colour or know what a date
 * is — a route arrives with it already on `properties.colour`, or without one
 * when there is nothing to spread across (see `routeColour` in `overlay.ts`).
 * It still carries no metric toggle, no palette picker and no playback: this
 * map answers "where do I fly", not "how did this one go".
 *
 * It is deliberately presentational: it is handed the routes to draw and says
 * which one the pointer is over. What that set *is* — every flight, or only
 * the ones matching a search — is the flights feature's business.
 */

const ROUTES_SOURCE_ID = 'routes';
const ROUTES_LAYER_ID = 'routes-line';
/** Invisible, wide, and the only thing hover and click actually hit. */
const ROUTES_HIT_LAYER_ID = 'routes-hit';
const ROUTES_ACTIVE_LAYER_ID = 'routes-active';
const HIT_WIDTH = 14;

/**
 * The track id a hit-layer feature carries, or `null` for no hit.
 *
 * MapLibre types feature properties as `any`, which is exactly the shape this
 * codebase's lint rules refuse to let through — so the value is read as
 * `unknown` and checked, once, here.
 */
type RenderedFeature = ReturnType<MapLibreMap['queryRenderedFeatures']>[number];

function trackIdOf(feature: RenderedFeature | undefined): string | null {
  const properties: unknown = feature?.properties;
  if (typeof properties !== 'object' || properties === null) {
    return null;
  }
  const value = (properties as Record<string, unknown>).trackId;
  return typeof value === 'string' ? value : null;
}

/**
 * Where the map looks before any flight has loaded: the New Zealand mainland,
 * which is who this app is for. It is only ever seen for the instant before
 * the first routes arrive and `fitBounds` takes over — a pilot with flights
 * never sees this framing, and a pilot with none sees the empty state instead.
 */
const INITIAL_CENTER: [number, number] = [173.5, -41];
const INITIAL_ZOOM = 4.2;

/** Never zoom past this when fitting to one short flight, or the fit reads as a mistake. */
const FIT_MAX_ZOOM = 11;

export interface TracksOverlayMapProps {
  routes: readonly TrackRoute[];
  /** The track drawn lifted out of the pile. Controlled, so a list hover could drive it too. */
  activeTrackId?: string | null;
  onActiveTrackId?: (trackId: string | null) => void;
  /** A click on a track — the flights page opens it. */
  onSelect?: (trackId: string) => void;
  /** Extra height for a page that can afford it. Tailwind classes, replacing the default. */
  heightClassName?: string;
}

export function TracksOverlayMap({
  routes,
  activeTrackId = null,
  onActiveTrackId,
  onSelect,
  heightClassName = 'h-[260px] sm:h-[340px]',
}: TracksOverlayMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const onActiveTrackIdRef = useRef(onActiveTrackId);
  const onSelectRef = useRef(onSelect);
  /** The bounds last fitted, so panning and zooming survives an unrelated redraw. */
  const fittedBoundsRef = useRef<string | null>(null);
  const styleLoadedRef = useRef(false);

  const [loaded, setLoaded] = useState(false);
  const [basemapId] = useState(() => preferredBasemapId());
  const [usingFallback, setUsingFallback] = useState(false);

  useEffect(() => {
    onActiveTrackIdRef.current = onActiveTrackId;
    onSelectRef.current = onSelect;
  });

  useEffect(() => {
    if (containerRef.current === null) {
      return;
    }
    const basemap = getBasemap(basemapId);
    const dark = prefersDark();
    const map = new MapLibreMapCtor({
      container: containerRef.current,
      style: basemap.style,
      center: INITIAL_CENTER,
      zoom: INITIAL_ZOOM,
      fadeDuration: motionDuration(300),
      // MapLibre's own attribution strip is white in both themes and sits
      // where this map's own credit line goes; `FlightMap` made the same
      // trade, and the credit is rendered below instead — never dropped.
      attributionControl: false,
    });
    mapRef.current = map;
    map.addControl(new NavigationControl({ showCompass: false }), 'top-right');

    const handleStyleReady = () => {
      if (map.getSource(ROUTES_SOURCE_ID) === undefined) {
        map.addSource(ROUTES_SOURCE_ID, {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: [] },
        });
        // A route's own colour wins when it has one; `coalesce` skips a
        // missing property (MapLibre reads it as `null`) rather than a route
        // that opted out of the date gradient falling back to black.
        const lineColour: ExpressionSpecification = [
          'coalesce',
          ['get', 'colour'],
          routeColour(dark),
        ];
        map.addLayer({
          id: ROUTES_LAYER_ID,
          type: 'line',
          source: ROUTES_SOURCE_ID,
          layout: { 'line-join': 'round', 'line-cap': 'round' },
          paint: {
            'line-color': lineColour,
            'line-width': ROUTE_WIDTH,
            'line-opacity': ROUTE_OPACITY,
          },
        });
        map.addLayer({
          id: ROUTES_ACTIVE_LAYER_ID,
          type: 'line',
          source: ROUTES_SOURCE_ID,
          layout: { 'line-join': 'round', 'line-cap': 'round' },
          filter: ['==', ['get', 'trackId'], ''],
          paint: {
            // Lifted out of the pile by width and opacity, same as before —
            // not by hue, or the hovered track would jump to a colour that no
            // longer says where it falls in the date range.
            'line-color': lineColour,
            'line-width': ROUTE_HIGHLIGHT_WIDTH,
            'line-opacity': 1,
          },
        });
        /*
         * A 1.6px line is not something a finger or a moving mouse can hit, so
         * hover and click are answered by a wide transparent copy of the same
         * geometry. Zero opacity still hit-tests; `visibility: none` would not,
         * which is the distinction that makes this work.
         */
        map.addLayer({
          id: ROUTES_HIT_LAYER_ID,
          type: 'line',
          source: ROUTES_SOURCE_ID,
          paint: { 'line-color': routeColour(dark), 'line-width': HIT_WIDTH, 'line-opacity': 0 },
        });
      }
      styleLoadedRef.current = true;
      setLoaded(true);
    };

    map.on('load', handleStyleReady);
    map.on('style.load', handleStyleReady);

    /*
     * A pointer over the map before its style has loaded has no hit layer to
     * query — MapLibre answers that with a console warning and an empty array,
     * which is noise about a state that resolves itself a moment later.
     */
    const handleMove = (event: MapMouseEvent) => {
      if (!styleLoadedRef.current) {
        return;
      }
      const trackId = trackIdOf(
        map.queryRenderedFeatures(event.point, { layers: [ROUTES_HIT_LAYER_ID] })[0],
      );
      map.getCanvas().style.cursor = trackId === null ? '' : 'pointer';
      onActiveTrackIdRef.current?.(trackId);
    };
    const handleLeave = () => {
      map.getCanvas().style.cursor = '';
      onActiveTrackIdRef.current?.(null);
    };
    const handleClick = (event: MapMouseEvent) => {
      if (!styleLoadedRef.current) {
        return;
      }
      const trackId = trackIdOf(
        map.queryRenderedFeatures(event.point, { layers: [ROUTES_HIT_LAYER_ID] })[0],
      );
      if (trackId !== null) {
        onSelectRef.current?.(trackId);
      }
    };

    map.on('mousemove', handleMove);
    map.on('mouseout', handleLeave);
    map.on('click', handleClick);

    return () => {
      map.remove();
      mapRef.current = null;
      styleLoadedRef.current = false;
      setLoaded(false);
    };
    // One map per mount; the basemap is chosen once, as on the flight page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*
   * One timeout, then the backup style — the same guarantee `FlightMap` makes,
   * for the same reason: a basemap host can start requiring a key overnight,
   * and what a pilot then sees should be a plainer map, not a grey rectangle.
   */
  useEffect(() => {
    const map = mapRef.current;
    const basemap = getBasemap(basemapId);
    const fallback = basemap.fallback;
    if (
      map === null ||
      typeof basemap.style !== 'string' ||
      fallback === undefined ||
      usingFallback
    ) {
      return;
    }
    const timer = window.setTimeout(() => {
      if (styleLoadedRef.current) {
        return;
      }
      setUsingFallback(true);
      styleLoadedRef.current = false;
      map.setStyle(fallback);
    }, STYLE_LOAD_TIMEOUT_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [basemapId, usingFallback, loaded]);

  // Redraw whenever the drawn set changes — a search narrowing the list
  // narrows this to the same flights.
  useEffect(() => {
    const map = mapRef.current;
    if (!loaded || map === null) {
      return;
    }
    const source = map.getSource<GeoJSONSource>(ROUTES_SOURCE_ID);
    if (source !== undefined) {
      void source.setData(buildRouteCollection(routes));
    }
  }, [loaded, routes]);

  // Fit to what is drawn, but only when what is drawn actually moved: a live
  // query re-firing must not yank the map back from wherever it was panned to.
  useEffect(() => {
    const map = mapRef.current;
    if (!loaded || map === null) {
      return;
    }
    const bounds = boundsForRoutes(routes);
    if (bounds === null) {
      return;
    }
    const key = bounds.map((value) => value.toFixed(4)).join(',');
    if (fittedBoundsRef.current === key) {
      return;
    }
    fittedBoundsRef.current = key;
    map.fitBounds(bounds, { padding: 36, maxZoom: FIT_MAX_ZOOM, duration: motionDuration(500) });
  }, [loaded, routes]);

  // Lift the hovered track out of the pile. A filter change is a paint-level
  // update, so this costs nothing next to re-sending the geometry.
  useEffect(() => {
    const map = mapRef.current;
    if (!loaded || map === null) {
      return;
    }
    map.setFilter(ROUTES_ACTIVE_LAYER_ID, ['==', ['get', 'trackId'], activeTrackId ?? '']);
  }, [loaded, activeTrackId]);

  const basemap = getBasemap(basemapId);
  const attribution =
    usingFallback && basemap.fallbackAttribution !== undefined
      ? basemap.fallbackAttribution
      : basemap.attribution;

  return (
    <div
      className={`border-border bg-surface-sunken rounded-container relative w-full overflow-hidden border ${heightClassName}`}
    >
      {/* Not `absolute inset-0` — see the same note in `FlightMap.tsx`. */}
      <div ref={containerRef} className="size-full" data-testid="tracks-overlay-canvas" />
      <p
        className="text-muted-foreground bg-surface/80 rounded-control absolute right-2 bottom-2 z-(--z-overlay) max-w-[60%] truncate px-1.5 py-0.5 text-[10px] leading-4 backdrop-blur"
        title={attribution}
        data-testid="tracks-overlay-attribution"
      >
        {attribution}
      </p>
    </div>
  );
}
